import { Directive, HostListener, ElementRef, Input } from '@angular/core';

@Directive({
  selector: '[appAllowOnlyNumber]',
  standalone: false
})
export class AllowOnlyNumberDirective {
  private _decimalPlaces: number = 6;
  @Input() allowLeadingZero: boolean = true;

  @Input() set decimalPlaces(val: number) {
    this._decimalPlaces = val;
    this.sanitize();
  }
  get decimalPlaces(): number {
    return this._decimalPlaces;
  }

  constructor(private readonly el: ElementRef) { }

  @HostListener('input', ['$event']) onInputChange(event: Event) {
    this.sanitize();
  }

  private sanitize() {
    const input = this.el.nativeElement;
    let sanitized = input.value.replaceAll(/[^0-9.]/g, '');

    // Ensure only one decimal point is allowed and handle decimal places
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      sanitized = parts[0] + '.' + parts.slice(1).join('');
    }

    if (parts.length >= 2) {
      const integerPart = parts[0];
      let decimalPart = parts[1];
      if (this.decimalPlaces > 0) {
        if (decimalPart.length > this.decimalPlaces) {
          decimalPart = decimalPart.substring(0, this.decimalPlaces);
        }
        sanitized = integerPart + '.' + decimalPart;
      } else {
        sanitized = integerPart;
      }
    }

    if (input.value !== sanitized) {
      input.value = sanitized;
      // Create and dispatch new input event so Angular updates the model
      const newEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(newEvent);
    }
  }

  @HostListener('blur', ['$event']) onBlur(event: Event) {
    const input = this.el.nativeElement;
    let value = input.value;

    if (value && !this.allowLeadingZero) {
      // Strip leading zeros unless followed by a dot or it's the only zero
      // Example: 007 -> 7, 00.5 -> 0.5, 0 -> 0
      let sanitized = value.replace(/^0+(?!\.|$)/, '');

      // Standardize values starting with a decimal point (e.g., .5 to 0.5)
      if (sanitized.startsWith('.')) {
        sanitized = '0' + sanitized;
      }

      if (input.value !== sanitized) {
        input.value = sanitized;
        // Create and dispatch new input event so Angular's FormControl updates
        const newEvent = new Event('input', { bubbles: true });
        input.dispatchEvent(newEvent);
      }
    }
  }

  @HostListener('paste', ['$event']) onPaste(event: ClipboardEvent) {
    const clipboardData = event.clipboardData;
    if (clipboardData) {
      const pastedText = clipboardData.getData('text');
      const regex = new RegExp(String.raw`^[0-9]*\.?[0-9]{0,${this.decimalPlaces}}$`);
      if (!regex.test(pastedText)) {
        event.preventDefault();
      }
    }
  }

  @HostListener('drop', ['$event']) onDrop(event: DragEvent) {
    const dataTransfer = event.dataTransfer;
    if (dataTransfer) {
      const droppedText = dataTransfer.getData('text');
      const regex = new RegExp(String.raw`^[0-9]*\.?[0-9]{0,${this.decimalPlaces}}$`);
      if (!regex.test(droppedText)) {
        event.preventDefault();
      }
    }
  }
}
